import { z } from "zod";
import {
  AREA_UNIT_ABBREVIATION,
  AreaUnitSchema,
  DIMENSION_UNIT_ABBREVIATION,
  DimensionUnitSchema,
  ITEM_UNIT_ABBREVIATION,
  ItemTypeSchema,
  ItemUnitConversionSchema,
  ItemUnitEnum,
  ItemUnitSchema,
  WEIGHT_UNIT_ABBREVIATION,
  WeightUnitSchema,
} from "./items";
import type { ItemUnit } from "./items";
import { ExternalMappingSchema } from "./externalMappings";

/** Weight-based pricing unit values. */
const WEIGHT_PRICING_VALUES = [...WeightUnitSchema.options] as const;

/** Length-based pricing unit values (includes MLF). */
const LENGTH_PRICING_VALUES = [...DimensionUnitSchema.options] as const;

/** Pricing unit for a price list line: weight, dimension (incl. MLF), area units, or each. */
export const PricingUnitSchema = z.enum([
  ...WEIGHT_PRICING_VALUES,
  ...LENGTH_PRICING_VALUES,
  ...AreaUnitSchema.options,
  ItemUnitEnum.each,
]);
export type PricingUnit = z.infer<typeof PricingUnitSchema>;

/** Enum-like accessor for PricingUnit values. */
export const PricingUnitEnum = PricingUnitSchema.enum;

/** Maps a PricingUnit value to its abbreviated display form (e.g. "kg", "lb", "ea"). */
export const PRICING_UNIT_ABBREVIATION: Record<PricingUnit, string> = {
  ...WEIGHT_UNIT_ABBREVIATION,
  ...DIMENSION_UNIT_ABBREVIATION,
  MLF: "MLF",
  ...AREA_UNIT_ABBREVIATION,
  [ItemUnitEnum.each]: "ea",
};

/**
 * Returns the display abbreviation for a pricing unit.
 * @param unit - The pricing unit to format
 * @returns The abbreviated label string (e.g. "ea", "kg", "MLF")
 */
export function formatPricingUnit(unit: PricingUnit): string {
  return PRICING_UNIT_ABBREVIATION[unit];
}

/**
 * Returns the display abbreviation for an item unit.
 * @param unit - The item unit to format
 * @returns The abbreviated label string (e.g. "ea", "kg", "ft")
 */
export function formatItemUnit(unit: ItemUnit): string {
  return ITEM_UNIT_ABBREVIATION[unit];
}

/** Source data that can supply a quoted unit price before any manual override. */
export const QuoteUnitPriceSourceSchema = z.enum(["ITEM", "PRICE_LIST"]);
export type QuoteUnitPriceSource = z.infer<typeof QuoteUnitPriceSourceSchema>;

/** Final resolution used to explain the effective unit price on a quote quantity break. */
export const QuoteUnitPriceResolutionSchema = z.enum([
  "ITEM",
  "PRICE_LIST",
  "MANUAL",
]);
export type QuoteUnitPriceResolution = z.infer<
  typeof QuoteUnitPriceResolutionSchema
>;

/** Quantity break used by a price list line to override a quote unit price. */
export const PriceListPriceBreakSchema = z.object({
  minQty: z.number().int().nonnegative(),
  unitPrice: z.string(),
});
export type PriceListPriceBreak = z.infer<typeof PriceListPriceBreakSchema>;

/** Lightweight item summary returned with hydrated price list lines. */
export const PriceListItemSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  revision: z.string(),
  type: ItemTypeSchema,
  unit: ItemUnitSchema.optional(),
  weightUnit: WeightUnitSchema.nullable().optional(),
  weight: z.string().nullable().optional(),
  dimensionUnit: DimensionUnitSchema.nullable().optional(),
  length: z.string().nullable().optional(),
  width: z.string().nullable().optional(),
  height: z.string().nullable().optional(),
  unitConversions: z.array(ItemUnitConversionSchema).optional(),
});
export type PriceListItemSummary = z.infer<typeof PriceListItemSummarySchema>;

/** Single price list line mapped to an item. */
export const PriceListLineSchema = z.object({
  id: z.string(),
  priceListId: z.string(),
  itemId: z.string(),
  item: PriceListItemSummarySchema.nullable().optional(),
  priceBreaks: z.array(PriceListPriceBreakSchema),
  unit: PricingUnitSchema.nullable().optional(),
  organizationId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type PriceListLine = z.infer<typeof PriceListLineSchema>;

/** Core price list schema returned from the API. */
export const PriceListSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  lineCount: z.number().optional(),
  externalMappings: z.array(ExternalMappingSchema).default([]),
  archivedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type PriceList = z.infer<typeof PriceListSchema>;

/** Payload for creating a single price list line. */
export const CreatePriceListLineSchema = z.object({
  itemId: z.string(),
  priceBreaks: z.array(PriceListPriceBreakSchema),
  unit: PricingUnitSchema.nullable().optional(),
});
export type CreatePriceListLine = z.infer<typeof CreatePriceListLineSchema>;

/** Payload for creating a price list. */
export const CreatePriceListParamsSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
});
export type CreatePriceListParams = z.infer<typeof CreatePriceListParamsSchema>;
export const CreatePriceListSchema = CreatePriceListParamsSchema;
export type CreatePriceList = CreatePriceListParams;

/** Payload for updating an existing price list. */
export const UpdatePriceListParamsSchema = z.object({
  name: z.string().optional(),
  description: z.string().nullable().optional(),
});
export type UpdatePriceListParams = z.infer<typeof UpdatePriceListParamsSchema>;
export const UpdatePriceListSchema = UpdatePriceListParamsSchema;
export type UpdatePriceList = UpdatePriceListParams;

/** Optional filter used when listing price lists. */
export const PriceListFilterSchema = z.object({
  search: z.string().optional(),
  includeArchived: z.boolean().optional(),
});
export type PriceListFilter = z.infer<typeof PriceListFilterSchema>;

/** Response returned after creating a price list. */
export const CreatePriceListResponseSchema = z.object({
  priceList: PriceListSchema,
});
export type CreatePriceListResponse = z.infer<
  typeof CreatePriceListResponseSchema
>;

/** Response returned when fetching a single price list. */
export const GetPriceListResponseSchema = z.object({
  priceList: PriceListSchema,
});
export type GetPriceListResponse = z.infer<typeof GetPriceListResponseSchema>;

/** Response returned after updating a price list. */
export const UpdatePriceListResponseSchema = z.object({
  priceList: PriceListSchema,
});
export type UpdatePriceListResponse = z.infer<
  typeof UpdatePriceListResponseSchema
>;

/** Response returned when listing price lists. */
export const ListPriceListsResponseSchema = z.object({
  priceLists: z.array(PriceListSchema),
});
export type ListPriceListsResponse = z.infer<
  typeof ListPriceListsResponseSchema
>;

/** Response returned when listing price lists associated with a company. */
export const ListAssociatedPriceListsResponseSchema = z.object({
  priceLists: z.array(PriceListSchema),
});
export type ListAssociatedPriceListsResponse = z.infer<
  typeof ListAssociatedPriceListsResponseSchema
>;

/** Response returned when resolving the current price list for a company. */
export const GetCurrentPriceListResponseSchema = z.object({
  priceList: PriceListSchema.nullable(),
});
export type GetCurrentPriceListResponse = z.infer<
  typeof GetCurrentPriceListResponseSchema
>;

/** Payload used to update the current company price-list association. */
export const SetCurrentPriceListParamsSchema = z.object({
  companyId: z.string(),
  priceListId: z.string().nullable(),
});
export type SetCurrentPriceListParams = z.infer<
  typeof SetCurrentPriceListParamsSchema
>;

/** Response returned after updating the current price list for a company. */
export const SetCurrentPriceListResponseSchema = z.object({
  priceList: PriceListSchema.nullable(),
});
export type SetCurrentPriceListResponse = z.infer<
  typeof SetCurrentPriceListResponseSchema
>;

/** Response returned after deleting a price list. */
export const DeletePriceListResponseSchema = z.object({
  success: z.literal(true),
});
export type DeletePriceListResponse = z.infer<
  typeof DeletePriceListResponseSchema
>;

/** Filter for paginated price list lines within a single price list. */
export const ListPriceListLinesParamsSchema = z.object({
  search: z.string().optional(),
  limit: z.number().int().positive().optional(),
  offset: z.number().int().nonnegative().optional(),
});
export type ListPriceListLinesParams = z.infer<
  typeof ListPriceListLinesParamsSchema
>;

/** Paginated response for price list lines. */
export const ListPriceListLinesResponseSchema = z.object({
  lines: z.array(PriceListLineSchema),
  total: z.number(),
});
export type ListPriceListLinesResponse = z.infer<
  typeof ListPriceListLinesResponseSchema
>;

/** Payload for batch-adding lines to a price list. */
export const AddPriceListLinesParamsSchema = z.object({
  lines: z.array(CreatePriceListLineSchema),
});
export type AddPriceListLinesParams = z.infer<
  typeof AddPriceListLinesParamsSchema
>;

/** Response after batch-adding lines to a price list. */
export const AddPriceListLinesResponseSchema = z.object({
  lines: z.array(PriceListLineSchema),
});
export type AddPriceListLinesResponse = z.infer<
  typeof AddPriceListLinesResponseSchema
>;

/** Payload for updating a single price list line. */
export const UpdatePriceListLineParamsSchema = z.object({
  itemId: z.string().optional(),
  priceBreaks: z.array(PriceListPriceBreakSchema).optional(),
  unit: PricingUnitSchema.nullable().optional(),
});
export type UpdatePriceListLineParams = z.infer<
  typeof UpdatePriceListLineParamsSchema
>;

/** Response after updating a single price list line. */
export const UpdatePriceListLineResponseSchema = z.object({
  line: PriceListLineSchema,
});
export type UpdatePriceListLineResponse = z.infer<
  typeof UpdatePriceListLineResponseSchema
>;

/** Payload for batch-removing lines from a price list. */
export const RemovePriceListLinesParamsSchema = z.object({
  lineIds: z.array(z.string()),
});
export type RemovePriceListLinesParams = z.infer<
  typeof RemovePriceListLinesParamsSchema
>;

/** Response after batch-removing lines from a price list. */
export const RemovePriceListLinesResponseSchema = z.object({
  removed: z.array(z.string()),
});
export type RemovePriceListLinesResponse = z.infer<
  typeof RemovePriceListLinesResponseSchema
>;
